import { CheckCircle, Building2, User, MapPin, Image, Edit2, Loader2 } from 'lucide-react';
import React, { useState } from 'react';
import { useOnboardingStore } from '../../../store/onboarding.store';
import { saveCompanyData } from '../../../utils/saveCompanyData';

interface ReviewStepProps {
  onNext: () => void;
  onBack: () => void;
  onEditStep: (step: number) => void;
}

export function ReviewStep({ onNext, onBack, onEditStep }: ReviewStepProps) {
  const { companyData, companyId, setCompanyId } = useOnboardingStore();
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSave = async () => {
    setSaving(true);
    setError(null);

    try {
      // Save all company data - UPDATE if companyId exists, CREATE if not
      const result = await saveCompanyData(companyId, {
        name: companyData.name,
        slogan: companyData.slogan || null,
        ein: companyData.ein || null,
        contactFirstName: companyData.contactFirstName || null,
        contactLastName: companyData.contactLastName || null,
        email: companyData.email || null,
        phone: companyData.phone || null,
        street: companyData.street || null,
        street2: companyData.street2 || null,
        city: companyData.city || null,
        state: companyData.state || null,
        zip: companyData.zip || null,
        latitude: companyData.latitude || null,
        longitude: companyData.longitude || null,
        place_id: companyData.place_id || null,
        logoUrl: companyData.logoUrl || null,
      });

      // Store companyId if it was just created
      if (result.created && result.companyId) {
        setCompanyId(result.companyId);
      }

      // Proceed to next section
      onNext();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An unexpected error occurred. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const sections = [
    {
      title: 'Business Details',
      icon: Building2,
      step: 0,
      items: [
        { label: 'Gym Name', value: companyData.name },
        { label: 'Slogan', value: companyData.slogan || '—' },
        { label: 'EIN', value: companyData.ein || '—' },
      ],
    },
    {
      title: 'Contact Information',
      icon: User,
      step: 1,
      items: [
        { label: 'Contact', value: `${companyData.contactFirstName} ${companyData.contactLastName}` },
        { label: 'Email', value: companyData.email },
        { label: 'Phone', value: companyData.phone },
      ],
    },
    {
      title: 'Business Address',
      icon: MapPin,
      step: 2,
      items: [
        { 
          label: 'Address', 
          value: `${companyData.street}${companyData.street2 ? `, ${companyData.street2}` : ''}` 
        },
        { label: 'City', value: `${companyData.city}, ${companyData.state} ${companyData.zip}` },
      ],
    },
    {
      title: 'Branding',
      icon: Image,
      step: 3,
      items: [
        { 
          label: 'Logo', 
          value: companyData.logoUrl ? 'Uploaded' : 'Not uploaded',
          isImage: !!companyData.logoUrl,
          imageUrl: companyData.logoUrl,
        },
      ],
    },
  ];

  return (
    <div className="space-y-6">
      <div className="text-center mb-8">
        <div className="w-16 h-16 rounded-full bg-green-600/20 flex items-center justify-center mx-auto mb-4">
          <CheckCircle size={32} className="text-green-500" />
        </div>
        <h2 className="text-2xl font-bold text-[#EBFFEE]">Review Your Information</h2>
        <p className="text-[#A9A3A3] mt-2">Make sure everything looks correct</p>
      </div>

      {/* Review Sections */}
      <div className="space-y-4">
        {sections.map((section) => (
          <div
            key={section.title}
            className="bg-[#363232] rounded-xl p-4"
          >
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <section.icon size={18} className="text-rollops-accent" />
                <h3 className="font-semibold text-[#EBFFEE]">{section.title}</h3>
              </div>
              <button
                type="button"
                onClick={() => onEditStep(section.step)}
                className="text-rollops-accent hover:text-[#EBFFEE] p-1 rounded transition-colors"
              >
                <Edit2 size={16} />
              </button>
            </div>
            <div className="space-y-2">
              {section.items.map((item: { label: string; value: string; isImage?: boolean; imageUrl?: string }) => (
                <div key={item.label} className="flex justify-between items-center">
                  <span className="text-sm text-[#A9A3A3]">{item.label}</span>
                  {item.isImage && item.imageUrl ? (
                    <img
                      src={item.imageUrl}
                      alt="Logo"
                      className="w-10 h-10 object-contain rounded bg-[#504A4A]"
                    />
                  ) : (
                    <span className="text-sm text-[#EBFFEE]">{item.value}</span>
                  )}
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>

      {error && (
        <div className="bg-red-900/20 border border-red-500/50 rounded-lg p-3">
          <p className="text-red-400 text-sm text-center">{error}</p>
        </div>
      )}

      {/* Navigation Buttons */}
      <div className="flex gap-4">
        <button
          type="button"
          onClick={onBack}
          disabled={saving}
          className="flex-1 py-3 px-4 bg-[#504A4A] hover:bg-[#5A5454] disabled:opacity-50 text-white font-semibold rounded-lg transition-colors"
        >
          Back
        </button>
        <button
          type="button"
          onClick={handleSave}
          disabled={saving}
          className="flex-1 py-3 px-4 bg-[#9B1421] hover:bg-[#7A0F1A] disabled:bg-[#504A4A] disabled:cursor-not-allowed text-white font-semibold rounded-lg transition-colors flex items-center justify-center gap-2"
        >
          {saving ? (
            <>
              <Loader2 size={18} className="animate-spin" />
              Creating Company...
            </>
          ) : (
            'Save & Continue'
          )}
        </button>
      </div>
    </div>
  );
}

